import { useEffect, useMemo, useState, useRef, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "@/hooks/useSearchParams"
import { isEqual } from "lodash"
import { useIsFetching } from "@tanstack/react-query"

import { AccountTypeKey } from "@/constants/account"
import LoadingSpinner from "@/app/components/LoadingSpinner"
import BasicButton from "@/components/button/BasicButton"
import Divider from "@/components/divider/Divider"
import Chip from "@/components/chip/Chip"
import ActivitySelectorDialog from "@/app/report/components/filter/activitySelector/ActivitySelectorDialog"
import SessionSelector from "@/app/report/components/filter/sessionSelector/SessionSelector"
import PlayerSelector from "@/app/report/components/filter/playerSelector/PlayerSelector"
import {
  REPORT_PADDING,
  DRAWER_WIDTH,
  ACTIVITY_SESSION_TYPE,
  REPORT_TYPE,
} from "@/app/report/common/constants"
import { ActivityDetail, ActivityParticipatedPlayerType } from "@/types/activity"
import { createDrawerStore } from "@/stores/global"
import {
  useActivityDetailQuery,
  useActivityParticipatedPlayerListQuery,
} from "@/hooks/api/useActivityQuery"
import { Session } from "@/types/session"
import useRouter from "@/hooks/router/useRoute"
import { globalDate } from "@/utils/format"

import "./reportFilter.scss"
import Icon from "@/components/icon/Icon.tsx"
import { colors } from "@/components/colors.ts"
import { MenuItem } from "@mui/material"
import Selector from "@/components/form/selector/Selector.tsx"
import {
  useReportTemplateMetasQuery,
  useUpdateReportTemplateLastUsedMutation,
} from "@/hooks/api/useReportQuery"
import { createAccountStore } from "@/stores/account.ts"

export default function ReportFilter({
  reportType,
  onClickViewReport,
  setSessionsInfo,
}: {
  reportType: REPORT_TYPE
  onClickViewReport?: () => void
  setSessionsInfo?: (sessions: Session[]) => void
}) {
  const { t } = useTranslation()
  const router = useRouter()
  const { isOpen: isDrawerOpen } = createDrawerStore()
  const { accountInfo } = createAccountStore((state) => ({ accountInfo: state.info }))

  const isPlayerAccount = accountInfo?.type === AccountTypeKey.Player
  const playerIdfAccount = accountInfo?.idfAccount

  const searchParams = useSearchParams()
  const urlDailyReportType = useMemo(() => searchParams.get("dailyReportType"), [searchParams])
  const urlIdfActivity = useMemo(
    () => searchParams.get("idfActivity")?.split(",").map(Number),
    [searchParams],
  )
  const urlDateStart = useMemo(() => searchParams.get("dateStart"), [searchParams])
  const urlDateFinish = useMemo(() => searchParams.get("dateFinish"), [searchParams])
  const urlIdfSessionList = useMemo(
    () => searchParams.get("idfSession")?.split(",").map(Number),
    [searchParams],
  )
  const urlIdfPlayerList = useMemo(
    () => searchParams.get("idfPlayer")?.split(",").map(Number),
    [searchParams],
  )
  const urlIdfTemplate = useMemo(
    () => searchParams.get("idfTemplate")?.split(",").map(Number),
    [searchParams],
  )

  const [activityDialogOpen, setActivityDialogOpen] = useState(false)
  const [selectedDateString, setSelectedDateString] = useState<string>("")
  const [selectedActivityChip, setSelectedActivityChip] = useState<string>("")

  const [selectedDate, setSelectedDate] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  })
  const [selectedActivity, setSelectedActivity] = useState<number[]>([])
  const [selectedType, setSelectedType] = useState<ACTIVITY_SESSION_TYPE>(
    urlDailyReportType === ACTIVITY_SESSION_TYPE.SESSION
      ? ACTIVITY_SESSION_TYPE.SESSION
      : ACTIVITY_SESSION_TYPE.ACTIVITY,
  )
  const [selectedSessionList, setSelectedSessionList] = useState<number[]>([])
  const [sessionSelectorOpen, setSessionSelectorOpen] = useState(false)
  const [selectedPlayerList, setSelectedPlayerList] = useState<ActivityParticipatedPlayerType[]>([])
  const [playerSelectorOpen, setPlayerSelectorOpen] = useState(false)
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false)
  const [selectedIdfTemplate, setSelectedIdfTemplate] = useState<number | null>(null)
  const filterRef = useRef<HTMLDivElement>(null)
  const hasAutoLoadedRef = useRef(false)
  const lastScrollY = useRef(0)

  const { data: templates } = useReportTemplateMetasQuery({
    reportType: reportType,
  })

  const { mutate: updateLastUsed } = useUpdateReportTemplateLastUsedMutation()

  const {
    data: detail,
    isSuccess,
    isError,
  } = useActivityDetailQuery(
    selectedActivity?.[0],
    !!selectedActivity?.length && reportType === REPORT_TYPE.DAILY,
  )

  const { data: participatedPlayerList } = useActivityParticipatedPlayerListQuery(
    selectedActivity ?? [],
    !!selectedActivity?.length && reportType === REPORT_TYPE.TREND,
  )

  const isFetching = useIsFetching()
  const isLoading = isFetching > 0

  const filteredPlayers = useMemo(() => {
    const playerList = reportType === REPORT_TYPE.DAILY ? detail?.players : participatedPlayerList
    if (!playerList) {
      return undefined
    }

    if (isPlayerAccount && playerIdfAccount) {
      return playerList.filter((player) => player.idfPlayer === playerIdfAccount)
    }

    return playerList
  }, [reportType, detail?.players, participatedPlayerList, isPlayerAccount, playerIdfAccount])

  const resetFilterWidth = useCallback(() => {
    const containerEl = document.querySelector(".Container")
    const sideDrawer = document.querySelector(".SideNavDrawerCont")

    // 모바일에서는 drawer가 overlay이므로 width 조정하지 않음
    const isMobileDrawer = sideDrawer && sideDrawer.classList.contains("isMobile")
    const isDrawerActuallyOpen =
      sideDrawer && sideDrawer.classList.contains("isDrawerOpen") && !isMobileDrawer

    if (containerEl && filterRef.current) {
      const containerWidth = containerEl.clientWidth

      if (isDrawerActuallyOpen) {
        filterRef.current.style.width = `${containerWidth - DRAWER_WIDTH - REPORT_PADDING}px`
        filterRef.current.style.left = `${DRAWER_WIDTH + 28}px`
      } else {
        filterRef.current.style.width = `${containerWidth - REPORT_PADDING}px`
        filterRef.current.style.left = `28px`
      }
    }
  }, [])

  useEffect(() => {
    if (urlIdfActivity && !isEqual(urlIdfActivity, selectedActivity)) {
      setSelectedActivity(urlIdfActivity)

      if (reportType === REPORT_TYPE.DAILY) {
        setSelectedSessionList([])
        setSelectedPlayerList([])
      }
    }

    if (urlDateStart && urlDateFinish && reportType === REPORT_TYPE.TREND) {
      if (urlDateStart !== selectedDate.start || urlDateFinish !== selectedDate.end) {
        setSelectedDate({
          start: urlDateStart,
          end: urlDateFinish,
        })
      }
    }

    if (urlDailyReportType) {
      const newType =
        urlDailyReportType === ACTIVITY_SESSION_TYPE.SESSION
          ? ACTIVITY_SESSION_TYPE.SESSION
          : ACTIVITY_SESSION_TYPE.ACTIVITY
      if (newType !== selectedType) {
        setSelectedType(newType)
      }
    }

    if (urlIdfSessionList && !isEqual(urlIdfSessionList, selectedSessionList)) {
      setSelectedSessionList(urlIdfSessionList)
    }

    if (urlIdfPlayerList && detail?.players) {
      const players = detail.players.filter((p) => urlIdfPlayerList.includes(p.idfPlayer))
      const currentPlayerIds = selectedPlayerList.map((p) => p.idfPlayer).sort()
      const newPlayerIds = players.map((p) => p.idfPlayer).sort()
      if (!isEqual(currentPlayerIds, newPlayerIds)) {
        setSelectedPlayerList(players)
      }
    }

    if (urlIdfTemplate) {
      if (urlIdfTemplate[0] !== selectedIdfTemplate) {
        setSelectedIdfTemplate(urlIdfTemplate[0])
      }
    } else if (templates && templates.length > 0 && !selectedIdfTemplate) {
      const lastUsedTemplate = templates.find((t) => t.isLastUsed)
      setSelectedIdfTemplate(lastUsedTemplate?.idfTemplate || templates[0].idfTemplate)
    }
  }, [
    urlIdfActivity,
    urlDateStart,
    urlDateFinish,
    urlDailyReportType,
    urlIdfSessionList,
    urlIdfPlayerList,
    urlIdfTemplate,
    templates,
    detail,
    reportType,
  ])

  useEffect(() => {
    const hasUrlParams = !!(urlIdfActivity && urlDateStart && urlDateFinish && urlIdfPlayerList)

    if (hasUrlParams) {
      onClickViewReport?.()
    }
  }, [searchParams])

  useEffect(() => {
    const hasUrlParams = !!(urlIdfActivity && urlDateStart && urlDateFinish)

    if (hasUrlParams) {
      // URL 파라미터로 이미 초기화된 진입(액티비티 → 리포트보기 등)은 auto-load 대상이 아니다.
      // 여기서 표시하지 않고 넘어가면, 이후 '리포트보기'로 URL 이 다시 쓰여 날짜가 빠지는 순간
      // hasUrlParams 가 false 로 뒤집히면서 auto-load 가 뒤늦게 발동해
      // 사용자가 고른 액티비티/세션 선택을 '세션 전체'로 덮어쓴다 (일감 #141).
      hasAutoLoadedRef.current = true

      return
    }

    if (hasAutoLoadedRef.current) {
      return
    }

    if (selectedActivity.length > 0 && detail && reportType === REPORT_TYPE.DAILY) {
      hasAutoLoadedRef.current = true

      if (detail.sessions?.length) {
        setSelectedType(ACTIVITY_SESSION_TYPE.SESSION)
        setSelectedSessionList(detail.sessions.map((session) => session.idfSession))
        setSessionsInfo?.(detail.sessions)
      } else {
        setSelectedType(ACTIVITY_SESSION_TYPE.ACTIVITY)
        setSelectedSessionList([])
      }

      if (detail.players?.length) {
        setSelectedPlayerList(detail.players)
      }

      setTimeout(() => {
        const params = new URLSearchParams()

        params.set("idfActivity", selectedActivity.join(","))
        params.set("dateStart", globalDate(detail.dateStart, true))
        params.set("dateFinish", globalDate(detail.dateFinish, true))

        if (detail.sessions?.length) {
          params.set("dailyReportType", ACTIVITY_SESSION_TYPE.SESSION)
          params.set("idfSession", detail.sessions.map((s) => s.idfSession).join(","))
        } else {
          params.set("dailyReportType", ACTIVITY_SESSION_TYPE.ACTIVITY)
        }

        if (detail.players?.length) {
          params.set("idfPlayer", detail.players.map((p) => p.idfPlayer).join(","))
        }

        if (selectedIdfTemplate) {
          params.set("idfTemplate", selectedIdfTemplate.toString())
        }

        window.history.replaceState(
          {},
          "",
          window.location.pathname + "?" + params.toString().replaceAll("%2C", ","),
        )
      }, 0)
    }
  }, [selectedActivity, detail, reportType, urlIdfActivity, urlDateStart, urlDateFinish])

  useEffect(() => {
    if (reportType !== REPORT_TYPE.DAILY || !detail || !urlIdfActivity) {
      return
    }

    const isActivityMatched = urlIdfActivity[0] === detail.idfActivity
    const hasSessionOrPlayerInUrl = !!(urlIdfSessionList || urlIdfPlayerList)

    if (!isActivityMatched || hasSessionOrPlayerInUrl) {
      return
    }

    const params = new URLSearchParams(window.location.search)

    params.set("dateStart", globalDate(detail.dateStart, true))
    params.set("dateFinish", globalDate(detail.dateFinish, true))

    if (detail.sessions?.length) {
      params.set("dailyReportType", ACTIVITY_SESSION_TYPE.SESSION)
      params.set("idfSession", detail.sessions.map((session) => session.idfSession).join(","))
      setSessionsInfo?.(detail.sessions)
    } else {
      params.set("dailyReportType", ACTIVITY_SESSION_TYPE.ACTIVITY)
    }

    if (detail.players?.length) {
      params.set("idfPlayer", detail.players.map((p) => p.idfPlayer).join(","))
    }

    if (selectedIdfTemplate) {
      params.set("idfTemplate", selectedIdfTemplate.toString())
    }

    window.history.replaceState(
      {},
      "",
      window.location.pathname + "?" + params.toString().replaceAll("%2C", ","),
    )
  }, [detail, urlIdfActivity, urlIdfSessionList, urlIdfPlayerList, selectedIdfTemplate, reportType])

  useEffect(() => {
    if (reportType !== REPORT_TYPE.DAILY || !detail) {
      return
    }

    if (detail.dateStart && detail.dateFinish) {
      const stringDateStart = globalDate(detail.dateStart, true)
      setSelectedDateString(stringDateStart)
    }

    if (detail.name) {
      setSelectedActivityChip(detail.name)
    }
  }, [detail, reportType])

  useEffect(() => {
    if (!participatedPlayerList || reportType !== REPORT_TYPE.TREND) {
      return
    }

    if (urlIdfPlayerList) {
      setSelectedPlayerList(
        participatedPlayerList.filter((player) => urlIdfPlayerList.includes(player.idfPlayer)),
      )
    } else if (!urlIdfPlayerList && selectedActivity.length > 0) {
      setSelectedPlayerList(participatedPlayerList)

      if (urlIdfActivity && urlDateStart && urlDateFinish && participatedPlayerList.length > 0) {
        const params = new URLSearchParams(window.location.search)
        params.set("idfPlayer", participatedPlayerList.map((p) => p.idfPlayer).join(","))

        if (selectedIdfTemplate) {
          params.set("idfTemplate", selectedIdfTemplate.toString())
        }

        window.history.replaceState(
          {},
          "",
          window.location.pathname + "?" + params.toString().replaceAll("%2C", ","),
        )
      }
    }

    if (urlDateStart && urlDateFinish) {
      setSelectedDateString(`${urlDateStart} ~ ${urlDateFinish}`)
    }
  }, [
    participatedPlayerList,
    reportType,
    urlIdfPlayerList,
    urlDateStart,
    urlDateFinish,
    urlIdfActivity,
    selectedIdfTemplate,
  ])

  useEffect(() => {
    const hasUrlParams = !!(urlIdfActivity && urlDateStart && urlDateFinish)

    if (hasUrlParams) {
      // URL 파라미터로 이미 초기화된 진입(액티비티 → 리포트보기 등)은 auto-load 대상이 아니다.
      // 여기서 표시하지 않고 넘어가면, 이후 '리포트보기'로 URL 이 다시 쓰여 날짜가 빠지는 순간
      // hasUrlParams 가 false 로 뒤집히면서 auto-load 가 뒤늦게 발동해
      // 사용자가 고른 액티비티/세션 선택을 '세션 전체'로 덮어쓴다 (일감 #141).
      hasAutoLoadedRef.current = true

      return
    }

    if (hasAutoLoadedRef.current) {
      return
    }

    if (
      reportType === REPORT_TYPE.TREND &&
      selectedActivity.length > 0 &&
      participatedPlayerList?.length &&
      selectedDate.start &&
      selectedDate.end &&
      selectedIdfTemplate
    ) {
      hasAutoLoadedRef.current = true

      setSelectedPlayerList(participatedPlayerList)

      setTimeout(() => {
        const params = new URLSearchParams()

        params.set("idfActivity", selectedActivity.join(","))
        params.set("dateStart", selectedDate.start)
        params.set("dateFinish", selectedDate.end)
        params.set("idfPlayer", participatedPlayerList.map((p) => p.idfPlayer).join(","))

        if (selectedIdfTemplate) {
          params.set("idfTemplate", selectedIdfTemplate.toString())
        }

        window.history.replaceState(
          {},
          "",
          window.location.pathname + "?" + params.toString().replaceAll("%2C", ","),
        )
      }, 0)
    }
  }, [
    selectedActivity,
    participatedPlayerList,
    selectedDate,
    reportType,
    urlIdfActivity,
    urlDateStart,
    urlDateFinish,
    selectedIdfTemplate,
  ])

  useEffect(() => {
    if (!isSuccess && isError) {
      router?.push(window.location.pathname)
    }
  }, [isSuccess, isError])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      setSessionSelectorOpen(false)
      setPlayerSelectorOpen(false)
      setTemplateSelectorOpen(false)

      if (filterRef.current) {
        if (currentScrollY > lastScrollY.current && currentScrollY > 80) {
          filterRef.current.classList.add("hidden")
        } else {
          filterRef.current.classList.remove("hidden")
        }
      }

      lastScrollY.current = currentScrollY
    }

    window.addEventListener("scroll", handleScroll)
    window.addEventListener("resize", resetFilterWidth)

    requestAnimationFrame(resetFilterWidth)

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", resetFilterWidth)
    }
  }, [resetFilterWidth])

  useEffect(() => {
    resetFilterWidth()
  }, [isDrawerOpen, resetFilterWidth])

  useEffect(() => {
    const isAnyOpen = sessionSelectorOpen || playerSelectorOpen || templateSelectorOpen

    if (isAnyOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    return () => {
      document.body.style.overflow = ""
    }
  }, [sessionSelectorOpen, playerSelectorOpen, templateSelectorOpen])

  useEffect(() => {
    const sideDrawer = document.querySelector(".SideNavDrawerCont")
    if (!sideDrawer) {
      return
    }

    const observer = new MutationObserver(() => {
      resetFilterWidth()
    })

    observer.observe(sideDrawer, {
      attributes: true,
      attributeFilter: ["class"],
    })

    return () => {
      observer.disconnect()
    }
  }, [resetFilterWidth])

  const handleViewReportClick = () => {
    const params = new URLSearchParams()

    if (selectedActivity.length) {
      params.set("idfActivity", selectedActivity.join(","))
    }
    // DAILY 는 URL 의 날짜를 selectedDate 에 하이드레이트하지 않는다 — selectedDate 는
    // 액티비티 선택 다이얼로그의 기본 조회 범위와 공유하는 state 라, 액티비티 1일치로
    // 좁아지면 다이얼로그에서 다른 액티비티를 못 고르게 된다.
    // 대신 URL 에 이미 있던 날짜를 그대로 이어써서 유실을 막는다 (일감 #141).
    const nextDateStart = selectedDate.start || urlDateStart
    const nextDateFinish = selectedDate.end || urlDateFinish

    if (nextDateStart && nextDateFinish) {
      params.set("dateStart", nextDateStart)
      params.set("dateFinish", nextDateFinish)
    }
    if (selectedType === "session") {
      params.set("dailyReportType", ACTIVITY_SESSION_TYPE.SESSION)
      if (selectedSessionList.length) {
        params.set(
          "idfSession",
          selectedSessionList.length > 1
            ? selectedSessionList.join(",")
            : selectedSessionList[0].toString(),
        )
      }
    } else {
      params.set("dailyReportType", ACTIVITY_SESSION_TYPE.ACTIVITY)
    }
    if (selectedPlayerList.length) {
      params.set(
        "idfPlayer",
        selectedPlayerList.length > 1
          ? selectedPlayerList.map((player) => player.idfPlayer).join(",")
          : selectedPlayerList[0].idfPlayer.toString(),
      )
    }
    if (selectedIdfTemplate) {
      params.set("idfTemplate", selectedIdfTemplate.toString())

      updateLastUsed({
        idfReportTemplate: selectedIdfTemplate,
        reportType: reportType === REPORT_TYPE.DAILY ? "daily" : "trend",
      })
    }

    window.history.replaceState(
      {},
      "",
      window.location.pathname + "?" + params.toString().replaceAll("%2C", ","),
    )
  }

  const isDisabledViewReportButton = useMemo(() => {
    const isEqualFilterActivityList = isEqual(urlIdfActivity, selectedActivity)
    const isEqualFilterActivity =
      reportType === REPORT_TYPE.TREND ||
      (selectedType === ACTIVITY_SESSION_TYPE.ACTIVITY && urlDailyReportType === selectedType)
    const isEqualFilterDate =
      reportType === REPORT_TYPE.DAILY
        ? true
        : selectedDate.start &&
          selectedDate.end &&
          urlDateStart &&
          urlDateFinish &&
          globalDate(selectedDate.start, true) === globalDate(urlDateStart, true) &&
          globalDate(selectedDate.end, true) === globalDate(urlDateFinish, true)

    const isEqualFilterSession =
      selectedType === ACTIVITY_SESSION_TYPE.SESSION &&
      urlDailyReportType === selectedType &&
      isEqual([...(urlIdfSessionList || [])].sort(), [...selectedSessionList].sort())

    const isEqualFilterPlayer = isEqual(
      [...(urlIdfPlayerList || [])].sort(),
      selectedPlayerList.map((player) => player.idfPlayer).sort(),
    )

    const isEqualTemplateId = urlIdfTemplate?.[0] === selectedIdfTemplate

    const isEmptyDate =
      reportType === REPORT_TYPE.DAILY
        ? false
        : selectedType === ACTIVITY_SESSION_TYPE.ACTIVITY
          ? !selectedDate.start
          : !selectedDate.start || !selectedDate.end

    const isEmptySessionList =
      selectedType === ACTIVITY_SESSION_TYPE.SESSION && !selectedSessionList.length

    const isEmptyPlayerList = !selectedPlayerList.length

    const allEqual =
      isEqualFilterActivityList &&
      (isEqualFilterActivity || isEqualFilterSession) &&
      isEqualFilterPlayer &&
      isEqualFilterDate &&
      isEqualTemplateId

    const hasEmpty = isEmptyDate || isEmptySessionList || isEmptyPlayerList

    return allEqual || hasEmpty
  }, [
    urlIdfActivity,
    selectedActivity,
    selectedDate,
    urlDailyReportType,
    selectedType,
    urlIdfSessionList,
    selectedSessionList,
    urlIdfPlayerList,
    selectedPlayerList,
    detail,
    urlIdfTemplate,
    selectedIdfTemplate,
    reportType,
    urlDateStart,
    urlDateFinish,
  ])

  const onChangeTemplate = (value: number | null) => {
    setTemplateSelectorOpen(false)
    setSelectedIdfTemplate(value)
  }

  return (
    <>
      <div className="ReportFilterCont" ref={filterRef}>
        <div className="FilterCont">
          <div className="report-activity-filter">
            <BasicButton
              type="default-outlined"
              size="md"
              onClick={() => {
                setActivityDialogOpen(true)
              }}>
              <div className="report-activity-filter-button">
                <span className="report-activity-filter-label">{t("activity")}</span>
                <Divider orientation="vertical" />
                <span className="report-activity-filter-date">{selectedDateString ?? ""}</span>
                {reportType === REPORT_TYPE.DAILY && !!selectedActivityChip.length && (
                  <Chip type="default-medium" label={selectedActivityChip} />
                )}
              </div>
            </BasicButton>
          </div>
          {reportType === REPORT_TYPE.DAILY && (
            <SessionSelector
              open={sessionSelectorOpen}
              setOpen={setSessionSelectorOpen}
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              selectedSessionList={selectedSessionList}
              setSelectedSessionList={setSelectedSessionList}
              detail={detail as ActivityDetail}
            />
          )}
          {!isPlayerAccount && (
            <div className="report-player-filter">
              <PlayerSelector
                open={playerSelectorOpen}
                setOpen={setPlayerSelectorOpen}
                selectedActivity={selectedActivity}
                players={filteredPlayers}
                selectedPlayerList={selectedPlayerList}
                setSelectedPlayerList={setSelectedPlayerList}
                paramsVersion={true}
              />
            </div>
          )}
          <div className="report-template-filter">
            <Selector
              type="select"
              open={templateSelectorOpen}
              onOpen={() => setTemplateSelectorOpen(true)}
              onClose={() => setTemplateSelectorOpen(false)}
              className="template-selector"
              value={selectedIdfTemplate || ""}
              onChange={(e: any) => onChangeTemplate(e.target.value as number)}
              renderValue={(value) => {
                const selectedTemplate = templates?.find((t) => t.idfTemplate === value)

                return (
                  <div className="selected-value">
                    <img src="/images/ri-table-line.png" alt="" className="template-icon" />
                    <span className="selected-text">{selectedTemplate?.name}</span>
                  </div>
                )
              }}>
              {templates?.map((t) => (
                <MenuItem
                  key={t.idfTemplate}
                  value={t.idfTemplate}
                  className="setting-template-menu">
                  <div className="item-left">
                    <span className="item-text">{t.name}</span>
                  </div>
                  {t.idfTemplate === selectedIdfTemplate && (
                    <div className="item-right">
                      <Icon type="check" size={16} color={colors.neutral.grey[1000]} />
                    </div>
                  )}
                </MenuItem>
              ))}
            </Selector>
          </div>
        </div>

        <div className="ReportButtonCont">
          <BasicButton
            type="primary-contained"
            size="md"
            onClick={handleViewReportClick}
            disabled={isDisabledViewReportButton}>
            {t("button.viewReport")}
          </BasicButton>
        </div>

        {isLoading && (
          <div className="ReportFilterLoadingOverlayCont">
            <LoadingSpinner />
          </div>
        )}
      </div>

      <ActivitySelectorDialog
        type={reportType}
        open={activityDialogOpen}
        setOpen={setActivityDialogOpen}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        setSelectedDateString={setSelectedDateString}
        setSelectedActivityChip={setSelectedActivityChip}
        selectedActivity={selectedActivity}
        setSelectedActivity={setSelectedActivity}
        playerIdfAccount={isPlayerAccount ? playerIdfAccount : undefined}
      />
    </>
  )
}
